import React, { FC, useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface EvenementBtns2Props {
  index: string;
  onBlocNotesClick?: (typeEvenement: string, idEvenement: number) => void;
  onConfirmClick?: (typeEvenement: string, idEvenement: number) => void;
  onInfoClick?: (
    placeId: string,
    placeNom: string,
    latitude: string,
    longitude: string
  ) => void;
  onRechercheClick?: (typeEvenement: string, idEvenement: number) => void;
  lienVisible?: string;
  lienItineraire?: string;
  placeId?: string;
  placeNom?: string;
  latitude?: string;
  longitude?: string;
  className?: string;
  optionsVisibles?: boolean;
  onCloseOptions?: () => void;
}

const EvenementBtns2: FC<EvenementBtns2Props> = ({
  index,
  onBlocNotesClick,
  onConfirmClick,
  onInfoClick,
  onRechercheClick,
  lienVisible,
  lienItineraire,
  placeId,
  placeNom,
  latitude,
  longitude,
  className = "right-2 top-2",
  optionsVisibles,
  onCloseOptions,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  let tailleTexte = "text-xs";
  let classeBouton = "w-6 h-6";
  let classePoints = "text-xl"

  if (
    location.pathname.startsWith("/itineraire-voyage-integre") ||
    location.pathname.startsWith("/en/travel-itinerary-embed")
  ) {
    tailleTexte = "text-xxs";
    classeBouton = "w-4 h-4";
    classePoints = "text-md"
  }

  const [isPopupVisible, setPopupVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const typeEvenement = index.split("-")[0];
  const idEvenement = Number(index.split("-")[1]);

  const hasActions =
    onRechercheClick ||
    onBlocNotesClick ||
    onConfirmClick ||
    onInfoClick ||
    lienVisible ||
    lienItineraire;

  useEffect(() => {
    setPopupVisible(optionsVisibles || false);
  }, [optionsVisibles]);

  const showPopup = () => {
    setPopupVisible(true);
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!popupRef.current?.contains(e.relatedTarget as Node)) {
      setPopupVisible(false);
    }
  };

const handleInfoClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  setPopupVisible(false);
  onCloseOptions?.();

  setTimeout(() => {
    if (onInfoClick && placeId && placeNom) {
      onInfoClick(placeId, placeNom, latitude || "", longitude || "");
    }
  }, 50);
};

const handleConfirmClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  setPopupVisible(false);
  onCloseOptions?.();

  setTimeout(() => {
    if (onConfirmClick && index) {
      onConfirmClick(typeEvenement, idEvenement);
    }
  }, 50);
};

const handleBlocClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  setPopupVisible(false);
  onCloseOptions?.();

  setTimeout(() => {
    if (onBlocNotesClick && index) {
      onBlocNotesClick(typeEvenement, idEvenement);
    }
  }, 50);
};

 const handleRechercheClick = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  setPopupVisible(false);
  onCloseOptions?.();

  setTimeout(() => {
    if (onRechercheClick) {
      onRechercheClick(typeEvenement, idEvenement);
    }
  }, 50);
};

  const actionBaseClass = `
    w-full

    flex
    items-center
    justify-between
    gap-3

    px-3
    py-2.5

    rounded-xl

    ${tailleTexte}
    font-medium
    text-neutral-900
    dark:text-white

    hover:bg-neutral-50
    dark:hover:bg-neutral-800

    transition-colors
    duration-150

    cursor-pointer
  `;

  return (
    <div
      className={`absolute z-[90] ${className}`}
      onMouseOver={showPopup}
      onMouseLeave={handleMouseLeave}
    >
      {/* BOUTON OPTIONS */}
      <button
        type="button"
        onMouseOver={showPopup}
        className={`
          ${classeBouton}

          rounded-full

          bg-white
          dark:bg-neutral-900

          border
          border-neutral-100
          dark:border-neutral-800

          shadow-sm
          hover:shadow-md

          flex
          items-center
          justify-center

          text-neutral-500
          hover:text-neutral-900
          dark:hover:text-white

          transition-all
          duration-200
        `}
      >
        <i className={`las la-ellipsis-v ${classePoints}`}/>
      </button>

      {/* MENU */}
      {isPopupVisible && hasActions && (
        <div
          ref={popupRef}
          className="
            absolute
            right-0
            top-0

            z-[100]

            w-[180px]

            rounded-2xl

            bg-white
            dark:bg-neutral-900

            border
            border-neutral-100
            dark:border-neutral-800

            shadow-xl

            p-1

            text-left
          "
          style={{ zIndex: 100 }}
        >
          <div
            className="
              px-3
              py-1

              text-[10px]
              font-bold
              uppercase
              tracking-wider

              text-neutral-400
              dark:text-neutral-500
            "
          >
            Options
          </div>

          {onRechercheClick && (
            <button
              type="button"
              className={`${actionBaseClass} hover:bg-rose-50 dark:hover:bg-neutral-800`}
              onClick={handleRechercheClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-search text-xs text-rose-500" />
                {t("reservation")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onBlocNotesClick && (
            <button
              type="button"
              className={actionBaseClass}
              onClick={handleBlocClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-pencil-alt text-xs text-amber-500" />
                {t("blocNotes")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onConfirmClick && (
            <button
              type="button"
              className={actionBaseClass}
              onClick={handleConfirmClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-file-alt text-xs text-blue-500" />
                {t("confirmation")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {onInfoClick && (
            <button
              type="button"
              className={actionBaseClass}
              onClick={handleInfoClick}
            >
              <span className="flex items-center gap-1">
                <i className="las la-info-circle text-xs text-violet-500" />
                {t("informations")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </button>
          )}

          {lienItineraire && (
            <a
              href={lienItineraire}
              target="_blank"
              rel="noreferrer"
              className={actionBaseClass}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="flex items-center gap-1">
                <i className="las la-globe text-xs text-neutral-700 dark:text-neutral-300" />
                {t("itineraire")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </a>
          )}

          {lienVisible && (
            <a
              href={lienVisible}
              target="_blank"
              rel="noreferrer"
              className={actionBaseClass}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="flex items-center gap-1">
                <i className="las la-link text-xs text-neutral-700 dark:text-neutral-300" />
                {t("lien")}
              </span>

              <i className="las la-angle-right text-neutral-300" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default EvenementBtns2;